














































































import { Component, Prop, Vue } from 'vue-property-decorator';
import permissionGuard from '@/guards/permission-guard';
import { SmartQuery } from 'vue-apollo-decorator';
import { GQL_yearlyReportForEmployee, GQL_yearlyReportForEmployeeVariables, GQL_yearlyReportForEmployee_yearlyReportForEmployee } from '@/queries/__generated__/yearlyReportForEmployee';
import {
  GQL_paychecksByYear,
  GQL_paychecksByYear_paychecksByYear,
  GQL_paychecksByYearVariables,
} from '@/queries/__generated__/paychecksByYear';
import { UNIT_PAYCHECK_SETTINGS } from '@/queries/CompanySetting';
import { GQL_unitPaycheckSetting_currentUnit } from '@/queries/__generated__/unitPaycheckSetting';
import WorkRelationReport from './WorkRelationReport.vue';
import sortBy from 'lodash/sortBy';
import YearSelect from '../YearSelect.vue';
import DownloadBase64FileButton from '@/components/DownloadBase64FileButton.vue';
import { GQL_yearlyReportForEmployeeWorkRelations, GQL_yearlyReportForEmployeeWorkRelations_unit_employees_workRelations } from '@/queries/__generated__/yearlyReportForEmployeeWorkRelations';
import gql from 'graphql-tag';

const YEARLY_REPORT_FOR_EMPLOYEE_REPORT = gql`
  query yearlyReportForEmployee($unitId: ID!, $year: Int!) {
    yearlyReportForEmployee(unitId: $unitId, year: $year) {
      workRelation {
        id
      }
      number
      description
      calculationType
      car {
        id
        name
        registrationNumber
      }
      project {
        id
        code
        name
      }
      department {
        id
        code
        name
        unit {
          id
        }
      }
      quantity
      rate
      sum
      vacationMoneyBaseAmount
    }
  }
`;

const YEARLY_PAYCHECKS = gql`
  query paychecksByYear($unitId: ID!, $year: Int!) {
    paychecksByYear(unitId: $unitId, year: $year) {
      id
      workRelation { id }
      number
      paidDate
      paycheckLines {
        id
        description
        sum
      }
    }
  }
`;

const YEARLY_REPORT_FOR_EMPLOYEE_REPORT_WORK_RELATIONS = gql`
  query yearlyReportForEmployeeWorkRelations($unitId: ID!) {
    unit(id: $unitId) {
      id
      employees {
        id
        workRelations {
          id
          shiftType
          employmentType
          occupation {
            id
            name
          }
          subUnit {
            id
            name
          }
          startDate
          employmentPercentage
          endDate
          endReason
          leaveAndLayoffs {
            id
            description
            startDate
            endDate
          }
          employee {
            id
            firstName
            lastName
            address
            zip
            city
            socialSecurityNumber
            temporarySocialSecurityNumber
          }
        }
      }
    }
  }
`;

const YEARLY_REPORT_FOR_EMPLOYEE_REPORT_PDF_BASE64 = gql`
  query yearlyReportForEmployeePdfBase64($workRelationId: ID!, $year: Int!) {
    workRelation(id: $workRelationId) {
      id
      yearlyReportForEmployeePdfBase64(year: $year)
    }
  }
`;

const SEND_YEARLY_REPORT_FOR_EMPLOYEE_TO_FB = gql`
  mutation sendYearlyReportForEmployeeToFb($unitId: ID!, $year: Int!) {
    sendYearlyReportForEmployeeToFb(input: { unitId: $unitId, year: $year }) {
      sentPdfs
    }
  }
`;

@Component({
  components: {
    YearSelect,
    WorkRelationReport,
    DownloadBase64FileButton,
  },
  beforeRouteEnter(to, from, next) {
    permissionGuard('canCreatePaychecks', to, from, next)
      .catch(router => {
        router.push({ name: 'Dashboard' });
        router.app.$sMessage.error(router.app.$t('noPermissions'));
      });
  },
})
export default class YearlyReportForEmployee extends Vue {
  @Prop({ type: Number, required: true }) private year!: number;

  @SmartQuery(UNIT_PAYCHECK_SETTINGS) private currentUnit?: GQL_unitPaycheckSetting_currentUnit;

  @SmartQuery<YearlyReportForEmployee, GQL_yearlyReportForEmployeeWorkRelations>({
    query: YEARLY_REPORT_FOR_EMPLOYEE_REPORT_WORK_RELATIONS,
    skip() { return !this.currentUnit },
    variables() { return { unitId: this.currentUnit!.id } },
    fetchPolicy: 'no-cache',
    update: data => data.unit!.employees.flatMap(e => e.workRelations),
  }) allWorkRelations!: GQL_yearlyReportForEmployeeWorkRelations_unit_employees_workRelations[];

  private SEND_YEARLY_REPORT_FOR_EMPLOYEE_TO_FB = SEND_YEARLY_REPORT_FOR_EMPLOYEE_TO_FB;
  private YEARLY_REPORT_FOR_EMPLOYEE_REPORT_PDF_BASE64 = YEARLY_REPORT_FOR_EMPLOYEE_REPORT_PDF_BASE64;

  private get workRelations(): GQL_yearlyReportForEmployeeWorkRelations_unit_employees_workRelations[] {
    return sortBy(this.allWorkRelations, wr => `${wr.employee.firstName} ${wr.employee.lastName} ${wr.id}`) // Sort by name
      .filter(wr => // Only active
        this.wrReportLines.get(wr.id) ||
        this.$dayjs.strict(wr.startDate).year() == this.year ||
        wr.endDate && this.$dayjs.strict(wr.endDate).year() == this.year ||
        this.$dayjs.strict(`${this.year}-01-01`).isBetween(wr.startDate, wr.endDate || '3000-01-01'),
      );
  }

  @SmartQuery<YearlyReportForEmployee, GQL_yearlyReportForEmployee, GQL_yearlyReportForEmployeeVariables>({
    query: YEARLY_REPORT_FOR_EMPLOYEE_REPORT,
    skip() { return !this.currentUnit },
    variables() {
      return {
        unitId: this.currentUnit!.id,
        year: this.year,
      };
    },
    update: data => data.yearlyReportForEmployee,
    fetchPolicy: 'no-cache',
    result({ data }) {
      this.wrReportLines = new Map(); // Reset

      sortBy(
        data.yearlyReportForEmployee,
        line => line.number,
      ).forEach(line => {
        this.wrReportLines.set(line.workRelation.id, (this.wrReportLines.get(line.workRelation.id) || []).concat([ line ]));
      });
    },
  }) private reportLines?: GQL_yearlyReportForEmployee_yearlyReportForEmployee[];

  @SmartQuery<YearlyReportForEmployee, GQL_paychecksByYear, GQL_paychecksByYearVariables>({
    query: YEARLY_PAYCHECKS,
    skip() { return !this.currentUnit },
    variables() {
      return {
        unitId: this.currentUnit!.id,
        year: this.year,
      };
    },
    fetchPolicy: 'no-cache',
    update: data => data.paychecksByYear,
    result({ data }) {
      this.wrPaychecksPaid = new Map();
      this.wrPaychecksUnpaid = new Map();

      data.paychecksByYear.forEach(paycheck => {
        const wrId = String(paycheck.workRelation.id);

        const paidYear = paycheck.paidDate ? this.$dayjs.strict(paycheck.paidDate).year() : null;

        if (paidYear === this.year) {
          this.wrPaychecksPaid.set(
            wrId,
            (this.wrPaychecksPaid.get(wrId) || []).concat(paycheck),
          );
        } else {
          this.wrPaychecksUnpaid.set(
            wrId,
            (this.wrPaychecksUnpaid.get(wrId) || []).concat(paycheck),
          );
        }
      });
    },
  })
  private paychecksByYear?: GQL_paychecksByYear_paychecksByYear[];

  private wrPaychecksPaid: Map<string, GQL_paychecksByYear_paychecksByYear[]> = new Map();
  private wrPaychecksUnpaid: Map<string, GQL_paychecksByYear_paychecksByYear[]> = new Map();

  private wrReportLines: Map<string, GQL_yearlyReportForEmployee_yearlyReportForEmployee[]> = new Map();
}
